import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";

// CSS styling
import "../../styles/activitiesPublic.css";

// Axios and API
import { fetchActivityDetail } from "../../api/fetchActivityDetail";

// Moment.js
import moment from "moment";

// Helpers
import { capitalizeFirstLetter } from "../../helpers/capitalizeFirstLetter.js";
import { absoluteUrl, activityPath, isoDate, plainText } from "../../helpers/seo";

// Components
import Seo, { organizationRef } from "../../components/Seo.js";
import Header from "../../components/Header.js";
import Footer from "../../components/Footer.js";
import ContainerText from "../../components/Container";

// React lottie
import Lottie from "react-lotties";
import PaperPlane from "../../lotties/paperplane.json";

const ActivitiyDetails = () => {
  const { activityId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [activity, setActivity] = useState(null);
  const [fullScreenImage, setFullScreenImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);

  // React lotties
  const lottieOptions = {
    loop: true,
    autoplay: true,
    animationData: PaperPlane,
    rendererSettings: {
      preserveAspectRatio: "xMidYMid slice",
    },
  };

  useEffect(() => {
    if (activityId) {
      fetchOneActivity(activityId);
    }
  }, [activityId]);

  const fetchOneActivity = async (activityId) => {
    setLoading(true);
    setNotFound(false);
    try {
      const response = await fetchActivityDetail(activityId);
      if (response && response.status === 200 && response.data) {
        const { data = {} } = response;
        setActivity(data);
      } else {
        setNotFound(true);
      }
    } catch (err) {
      console.log(err);
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  };

  // click to view image full screen
  const openFullScreen = (imageUrl) => {
    setFullScreenImage(imageUrl);
  };

  // click to close image full screen
  const closeFullScreen = () => {
    setFullScreenImage(null);
  };

  // Old /activities/88 links (and activities whose title changed) move to /activities/88/<slug>.
  useEffect(() => {
    if (
      activity &&
      String(activity.id) === String(activityId) &&
      location.pathname !== activityPath(activity)
    ) {
      navigate(activityPath(activity), { replace: true });
    }
  }, [activity, activityId, location.pathname]);

  const seoPath = activity ? activityPath(activity) : `/activities/${activityId}`;
  const seoTitle = activity ? capitalizeFirstLetter(activity.title) : "";
  const seoDescription = activity
    ? [activity.description, activity.input_summary]
        .map((text) => plainText(text, 160))
        .reduce((best, text) => (best.length >= 70 ? best : text || best), "")
    : "";
  const seoImage =
    activity && Array.isArray(activity.ActivityImages)
      ? (activity.ActivityImages[0] || {}).img_url
      : undefined;

  return (
    <div id="activityPublic">
      {activity && !loading ? (
        <Seo
          title={seoTitle}
          description={seoDescription}
          path={seoPath}
          image={seoImage}
          imageAlt={seoTitle}
          type="article"
          breadcrumbs={[
            { name: "Activities", path: "/activities" },
            { name: seoTitle, path: seoPath },
          ]}
          article={{
            publishedTime: isoDate(activity.activityDate || activity.createdAt),
            modifiedTime: isoDate(activity.updatedAt),
            section: capitalizeFirstLetter(activity.activityType),
          }}
          jsonLd={{
            "@type": "Article",
            headline: seoTitle,
            description: seoDescription,
            image: seoImage ? [absoluteUrl(seoImage)] : undefined,
            datePublished: isoDate(activity.activityDate || activity.createdAt),
            dateModified: isoDate(activity.updatedAt || activity.createdAt),
            author: organizationRef,
            publisher: organizationRef,
            mainEntityOfPage: absoluteUrl(seoPath),
            inLanguage: "en",
          }}
        />
      ) : notFound && !loading ? (
        <Seo title="Activity not found" noindex />
      ) : null}
      <Header />
      {loading ? (
        <div
          style={{
            paddingTop: "15em",
            paddingBottom: "5em",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            textAlign: "center",
          }}
        >
          <Lottie options={lottieOptions} height={200} width={400} />
          <h4
            style={{
              color: "#0D4C86",
            }}
          >
            LOADING
          </h4>
          <p
            style={{
              color: "#0D4C86",
            }}
          >
            Processing your request, please wait...
          </p>
        </div>
      ) : activity ? (
        <div className="activityPublicContainer">
          <ContainerText
            props={
              <div className="activityPublicDetailCardContainerDesc">
                <div
                  className="previousButton"
                  onClick={() => navigate("/activities")}
                  style={{ marginTop: "4em" }}
                >
                  <span className="expertiseArrowRight">
                    <i className="fas fa-angle-left" />
                  </span>
                  <Link to="/activities" onClick={(e) => e.stopPropagation()}>
                    Back to previous page
                  </Link>
                </div>
                {activity.title && activity.activityDate ? (
                  <div className="activityPublicCardTitle">
                    <div className="activityPublicCardDescription paragrafDesc">
                      {moment(activity.activityDate).format("DD/MM/YYYY")}
                    </div>
                    <h1 className="headerDescription seoH1Sized">
                      {capitalizeFirstLetter(activity.title)}
                    </h1>
                  </div>
                ) : null}
                {activity.activityType ? (
                  <div
                    className="activityPublicCardType title"
                    style={{ lineHeight: "30px" }}
                  >
                    <p>{capitalizeFirstLetter(activity.activityType)}</p>
                  </div>
                ) : null}
                {activity.description ? (
                  <div
                    className="activityPublicCardDescription paragraf"
                    style={{ lineHeight: "30px" }}
                  >
                    <p>{activity.description}</p>
                  </div>
                ) : null}
              </div>
            }
          />
          <ContainerText
            props={
              <div className="activityPublicDetailCardContainer">
                <div className="activityPublicCards">
                  <div className="activityPublicEachCard">
                    {activity &&
                    activity.ActivityTags &&
                    Array.isArray(activity.ActivityTags) &&
                    activity.ActivityTags.length > 0 ? (
                      <div style={{ marginTop: "20px" }}>
                        <div className="blogCardTags title">Tags:</div>
                        {activity.ActivityTags.map((tag, index) => {
                          if (tag && tag.TagId) {
                            const { Tag: { tag_name } = {} } = tag;
                            return (
                              <div
                                className="blogCardTagsBoxContainer"
                                key={index}
                              >
                                <div className="blogCardTagsBox paragraf">
                                  <p>{capitalizeFirstLetter(tag_name)}</p>
                                </div>
                              </div>
                            );
                          } else {
                            return null;
                          }
                        })}
                      </div>
                    ) : null}
                  </div>
                  <div className="activityPublicEachCard">
                    {activity && activity.link ? (
                      <div style={{ marginTop: "20px" }}>
                        <div className="blogCardTags title">Link</div>
                        <div className="blogCardTagsBoxContainer paragraf">
                          <a
                            href={`https://${activity.link}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {activity.link}
                          </a>
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            }
          />
          <ContainerText
            props={
              <div className="activityPublicCardContainer">
                <div className="activityPublicCards">
                  {activity &&
                    activity.ActivityImages &&
                    Array.isArray(activity.ActivityImages) &&
                    activity.ActivityImages.length > 0 &&
                    activity.ActivityImages.map((activityImages, index) => {
                      const { img_url } = activityImages || {};
                      return (
                        <div key={index} className="activityPublicEachCard">
                          <div className="activityPublicCardPicture">
                            <img
                              src={img_url}
                              alt={
                                index === 0
                                  ? seoTitle
                                  : `${seoTitle} (${index + 1})`
                              }
                              loading={index === 0 ? undefined : "lazy"}
                              onClick={() => openFullScreen(img_url)}
                            />
                          </div>
                        </div>
                      );
                    })}
                  {fullScreenImage && (
                    <div
                      className="image-viewer full-screen"
                      onClick={closeFullScreen}
                      onKeyDown={closeFullScreen}
                      tabIndex={0}
                    >
                      <div className="image-container">
                        <img src={fullScreenImage} alt={seoTitle} />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            }
          />
        </div>
      ) : null}
      <Footer />
    </div>
  );
};

export default ActivitiyDetails;
